import { query } from 'bitecs';
import { AudioDirector } from '@src/audio/AudioDirector';
import { CommercialCutscene } from '@src/boot/CommercialCutscene';
import { DeathCinematic } from '@src/boot/DeathCinematic';
import { VictoryCinematic } from '@src/boot/VictoryCinematic';
import { openingCut, playOpeningCinematic, prefetchOpeningArt } from '@src/boot/OpeningCinematic';
import { playOpeningCurtain } from '@src/boot/OpeningCurtain';
import { PauseMenu } from '@src/boot/PauseMenu';
import { bindVisualViewportFill } from '@src/boot/fullscreen';
import { showStartScreen, type StartScreenHandle } from '@src/boot/StartScreen';
import { difficulty, loadDifficulty } from '@src/core/Difficulty';
import { prefetchLeaderboard, submitRunScore, type RunOutcome } from '@src/leaderboard';
import { GameLoop } from '@src/core/GameLoop';
import { spawnPlayer } from '@src/ecs/archetypes';
import { Health, Player } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { CollisionSystem } from '@src/ecs/systems/CollisionSystem';
import { DamageSystem } from '@src/ecs/systems/DamageSystem';
import { MineSystem } from '@src/ecs/systems/MineSystem';
import { OffhandSystem } from '@src/ecs/systems/OffhandSystem';
import { EnemyAISystem } from '@src/ecs/systems/EnemyAISystem';
import { WallEnemySystem } from '@src/ecs/systems/WallEnemySystem';
import { LifetimeSystem } from '@src/ecs/systems/LifetimeSystem';
import { MovementSystem } from '@src/ecs/systems/MovementSystem';
import { PickupSystem } from '@src/ecs/systems/PickupSystem';
import { ProjectileSystem } from '@src/ecs/systems/ProjectileSystem';
import { SpawnerSystem } from '@src/ecs/systems/SpawnerSystem';
import { WeaponSystem } from '@src/ecs/systems/WeaponSystem';
import { AnimationSystem } from '@src/ecs/systems/AnimationSystem';
import { createGameContext, RoomPhase } from '@src/ecs/world';
import { BossSystem } from '@src/game/Boss';
import { DragonSystem } from '@src/game/Dragon';
import { RoomDirector } from '@src/game/RoomDirector';
import { commercialById, pickNextCommercial } from '@src/game/SponsorCommercials';
import { loadArenaArt } from '@src/graphics/ArenaArt';
import { loadAtlas } from '@src/graphics/AtlasLoader';
import { Hud } from '@src/graphics/Hud';
import { RenderSyncSystem } from '@src/graphics/RenderSyncSystem';
import { ScreenFx } from '@src/graphics/ScreenFx';
import { InputSystem } from '@src/input/InputSystem';
import '@src/styles.css';

function byId<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

function resetBootLayer(boot: HTMLElement, beat: HTMLElement, status: HTMLElement): void {
  boot.classList.remove('leaving', 'hidden');
  // Clear residual curtain styles so the title screen can fade in again.
  boot.style.animation = 'none';
  boot.style.background = '';
  void boot.offsetWidth;
  boot.style.animation = '';
  beat.classList.remove('show');
  status.textContent = '';
  status.className = 'title-status';
  if (!status.isConnected) boot.appendChild(status);
}

async function boot(): Promise<void> {
  bindVisualViewportFill();
  const canvas = byId<HTMLCanvasElement>('game');
  const hudRoot = byId<HTMLDivElement>('hud');
  const bootScreen = byId<HTMLDivElement>('boot');
  const bootBeat = byId<HTMLDivElement>('boot-beat');
  const status = byId<HTMLParagraphElement>('boot-status');

  status.textContent = 'Summoning the renderer...';
  loadDifficulty();
  prefetchLeaderboard();

  // Audio graph first so the start screen can unlock it on the very first gesture.
  const audio = new AudioDirector();
  const startAudio = {
    announcer: audio.announcer,
    sfx: audio.sfx,
    playMenuMusic: () => audio.playMenuMusic(),
    leaveMenu: () => audio.leaveMenu(),
    audioRunning: () => audio.buses.context.state === 'running',
  };

  const firstStart = showStartScreen({
    host: bootScreen,
    status,
    audio: startAudio,
  });

  if (!('gpu' in navigator)) {
    status.textContent = 'WebGPU not detected; falling back to WebGL2.';
  }

  const [atlas, arenaArt] = await Promise.all([loadAtlas(), loadArenaArt()]);
  const render = new RenderSyncSystem(canvas);
  const backend = await render.setup(atlas, arenaArt);
  status.textContent = `${backend} ready. ${atlas ? 'Sprite atlas loaded.' : 'No atlas yet: procedural sprites.'}${arenaArt ? '' : ' Procedural arena.'}`;

  const ctx = createGameContext((Date.now() & 0xffffffff) >>> 0);
  const input = new InputSystem(canvas);
  input.setPointerMapper((sx, sy, out) => render.screenToWorld(sx, sy, out));
  const roomDirector = new RoomDirector();
  const hud = new Hud(hudRoot);

  const playCommercial = (onFinished?: () => void): void => {
    const preferId = new URLSearchParams(window.location.search).get('commercial');
    const commercial = (preferId && commercialById(preferId)) || pickNextCommercial();
    input.setTouchControlsVisible(false);
    if (inArena) pause.disarm();
    const held = query(ctx.world, [Player]);
    for (let i = 0; i < held.length; i++) Player.firing[held[i]] = 0;
    loop.pause();
    audio.beginCommercialBreak();
    new CommercialCutscene({
      host: document.body,
      sfx: audio.sfx,
      announcer: audio.announcer,
      crowd: audio.crowd,
      commercial,
      onComplete: () => {
        audio.endCommercialBreak();
        if (inArena && !pause.isOpen()) {
          loop.resume();
          pause.arm();
          input.setTouchControlsVisible(true);
        }
        const players = query(ctx.world, [Player, Health]);
        if (players.length > 0 && !Player.dead[players[0]]) {
          const pid = players[0];
          if (commercial.bonus.hp) {
            Health.hp[pid] = Math.min(Health.max[pid], Health.hp[pid] + commercial.bonus.hp);
          }
        }
        if (commercial.bonus.score) {
          ctx.score += commercial.bonus.score;
        }
        if (commercial.bonus.gems) {
          ctx.score += commercial.bonus.gems * 10;
        }
        onFinished?.();
      },
    });
  };

  roomDirector.onCommercialBreak = (onComplete) => {
    playCommercial(onComplete);
  };

  const bootParams = new URLSearchParams(window.location.search);
  const previewDeath = bootParams.has('death');
  const previewVictory = bootParams.has('victory') || bootParams.has('ending');

  const simSystems: GameSystem[] = [
    input,
    new MovementSystem(),
    new WeaponSystem(),
    new ProjectileSystem(),
    new SpawnerSystem(),
    new EnemyAISystem(),
    new CollisionSystem(),
    new MineSystem(),
    new WallEnemySystem(),
    new DamageSystem(),
    new PickupSystem(),
    new OffhandSystem(),
    roomDirector,
    new DragonSystem(),
    new BossSystem(),
    new AnimationSystem(),
    new LifetimeSystem(),
  ];

  const loop = new GameLoop({
    tick(dt) {
      ctx.time += dt;
      for (let i = 0; i < simSystems.length; i++) simSystems[i].update(dt);
    },
    render(alpha, frameDt) {
      screenFx.update(frameDt);
      render.update(alpha, frameDt);
      hud.update(frameDt);
      audio.update(frameDt);
      ctx.events.clear();
    },
  });
  const screenFx = new ScreenFx(loop, render.arenaCamera);

  const presentation: GameSystem[] = [render, screenFx, hud, audio];
  for (const s of simSystems) s.init(ctx);
  for (const s of presentation) s.init(ctx);
  hud.setProjector((wx, wy, out) => render.arenaCamera.worldToScreen(wx, wy, out));

  spawnPlayer(ctx, 0, 0, 0);

  let inArena = false;
  let titleWait: (() => void) | null = null;
  let scoreSent = false;

  const recordFinishedRun = (outcome: RunOutcome): void => {
    if (scoreSent) return;
    scoreSent = true;
    void submitRunScore({
      score: ctx.score,
      floor: ctx.floor.floor,
      room: ctx.room.roomIndex,
      kills: ctx.kills,
      combo: ctx.combo.best,
      tier: difficulty().id,
      outcome,
    });
  };

  const waitForTitleRequest = (): Promise<void> =>
    new Promise((resolve) => {
      titleWait = resolve;
    });

  let deathCine: DeathCinematic | null = null;
  let deathCineTimer = 0;
  let deathCinePlaying = false;
  let victoryCine: VictoryCinematic | null = null;
  let victoryCineTimer = 0;
  let victoryCinePlaying = false;

  const finishDeathCinematicHandoff = (): void => {
    deathCine = null;
    deathCinePlaying = false;
    audio.endDeathCinematic();
    if (inArena && ctx.room.phase === RoomPhase.GameOver) {
      hud.releaseGameOverOverlay();
    } else {
      hud.clearGameOverHold();
    }
    if (inArena) {
      pause.arm();
      input.setTouchControlsVisible(true);
    }
  };

  const playDeathCinematic = (onFinished?: () => void, immediate = false, sceneId?: string): void => {
    if (deathCinePlaying) return;
    deathCinePlaying = true;
    hud.holdGameOverOverlay();
    audio.suppressNextDeathAnnouncer();
    input.setTouchControlsVisible(false);
    if (inArena) pause.disarm();

    const start = (): void => {
      deathCineTimer = 0;
      void audio.buses.resume().catch(() => undefined);
      audio.beginDeathCinematic();
      deathCine = new DeathCinematic({
        host: document.body,
        sfx: audio.sfx,
        crowd: audio.crowd,
        ...(sceneId ? { sceneId } : {}),
        onComplete: () => {
          finishDeathCinematicHandoff();
          onFinished?.();
        },
      });
    };

    if (immediate) start();
    else deathCineTimer = window.setTimeout(start, 320);
  };

  const cancelDeathCinematic = (): void => {
    if (deathCineTimer) {
      window.clearTimeout(deathCineTimer);
      deathCineTimer = 0;
    }
    if (deathCine) {
      deathCine.finish();
      return;
    }
    if (deathCinePlaying) finishDeathCinematicHandoff();
  };

  const finishVictoryCinematicHandoff = (): void => {
    victoryCine = null;
    victoryCinePlaying = false;
    audio.endVictoryCinematic();
    hudRoot.style.visibility = '';
    if (!inArena) bootScreen.classList.remove('hidden');
    if (inArena && ctx.room.phase === RoomPhase.Victory) {
      hud.releaseGameOverOverlay();
    } else {
      hud.clearGameOverHold();
    }
    if (inArena) {
      pause.arm();
      input.setTouchControlsVisible(true);
    }
  };

  const playVictoryCinematic = (onFinished?: () => void, immediate = false): void => {
    if (victoryCinePlaying || deathCinePlaying) return;
    victoryCinePlaying = true;
    hud.holdGameOverOverlay();
    hudRoot.style.visibility = 'hidden';
    input.setTouchControlsVisible(false);
    if (inArena) pause.disarm();
    else bootScreen.classList.add('hidden');

    const start = (): void => {
      victoryCineTimer = 0;
      void audio.buses.resume().catch(() => undefined);
      audio.beginVictoryCinematic();
      victoryCine = new VictoryCinematic({
        host: document.body,
        sfx: audio.sfx,
        crowd: audio.crowd,
        stats: {
          score: ctx.score,
          kills: ctx.kills,
          combo: ctx.combo.best,
          floor: Math.max(1, ctx.floor.floor),
          rooms: ctx.room.roomIndex,
        },
        onComplete: () => {
          finishVictoryCinematicHandoff();
          onFinished?.();
        },
      });
    };

    if (immediate) start();
    else victoryCineTimer = window.setTimeout(start, 480);
  };

  const cancelVictoryCinematic = (): void => {
    if (victoryCineTimer) {
      window.clearTimeout(victoryCineTimer);
      victoryCineTimer = 0;
    }
    if (victoryCine) {
      victoryCine.finish();
      return;
    }
    if (victoryCinePlaying) finishVictoryCinematicHandoff();
  };

  let continueComplete: (() => void) | null = null;

  const cancelContinuePrompt = (): void => {
    continueComplete = null;
    hud.hideContinue();
    ctx.continuePrompt = false;
  };

  const restartRun = (): void => {
    cancelContinuePrompt();
    cancelDeathCinematic();
    cancelVictoryCinematic();
    audio.silenceArena();
    scoreSent = false;
    roomDirector.startRun();
    if (!loop.isRunning) loop.start();
    loop.resume();
    if (inArena) input.setTouchControlsVisible(true);
  };

  const goToTitle = (): void => {
    cancelContinuePrompt();
    cancelDeathCinematic();
    cancelVictoryCinematic();
    input.setTouchControlsVisible(false);
    pause.disarm();
    audio.returnToMenu();
    roomDirector.abandonRun();
    hud.hideCenter();
    loop.pause();
    resetBootLayer(bootScreen, bootBeat, status);
    inArena = false;
    const done = titleWait;
    titleWait = null;
    done?.();
  };

  const pause = new PauseMenu({
    host: document.body,
    audio,
    pauseWorld: () => {
      input.setTouchControlsVisible(false);
      loop.pause();
      audio.pauseCombat();
    },
    resumeWorld: () => {
      audio.resumeCombat();
      loop.resume();
      if (inArena) {
        input.setTouchControlsVisible(true);
      }
    },
    onRestart: restartRun,
    onTitle: goToTitle,
  });

  hud.onRestart = restartRun;
  hud.onTitle = goToTitle;
  hud.onContinue = () => {
    const done = continueComplete;
    continueComplete = null;
    done?.();
    audio.resumeAfterContinue();
    if (inArena) {
      loop.resume();
      pause.arm();
      input.setTouchControlsVisible(true);
    }
  };
  hud.onContinueExpire = () => {
    continueComplete = null;
    roomDirector.expireContinue();
    if (inArena) {
      loop.resume();
      input.setTouchControlsVisible(false);
    }
  };
  hud.onPause = () => {
    if (
      inArena &&
      !deathCinePlaying &&
      !victoryCinePlaying &&
      !ctx.continuePrompt &&
      !roomDirector.isCommercialActive() &&
      !pause.isOpen()
    ) {
      pause.open();
    }
  };
  window.addEventListener('keydown', (e) => {
    if (
      e.code === 'KeyR' &&
      inArena &&
      (ctx.room.phase === RoomPhase.GameOver || ctx.room.phase === RoomPhase.Victory) &&
      !deathCinePlaying &&
      !victoryCinePlaying &&
      !pause.isOpen()
    ) {
      restartRun();
    }
  });

  roomDirector.onContinuePrompt = (onComplete) => {
    continueComplete = onComplete;
    if (inArena) pause.disarm();
    input.setTouchControlsVisible(true);
    loop.pause();
    hud.showContinuePrompt(ctx.continues);
  };
  roomDirector.onDeathCinematic = (onComplete) => {
    recordFinishedRun('death');
    playDeathCinematic(onComplete);
  };
  roomDirector.onVictoryCinematic = (onComplete) => {
    recordFinishedRun('victory');
    playVictoryCinematic(onComplete);
  };
  if (previewVictory) playVictoryCinematic(undefined, true);
  else if (previewDeath) playDeathCinematic(undefined, true, bootParams.get('death') || undefined);
  else if (bootParams.has('commercial')) playCommercial();

  const orientationOverlay = document.getElementById('orientation-overlay');
  const updateOrientation = (): void => {
    if (!orientationOverlay) return;
    const isPortrait = window.innerHeight > window.innerWidth;
    orientationOverlay.classList.toggle('active', isPortrait);
  };
  window.addEventListener('resize', updateOrientation);
  window.addEventListener('orientationchange', updateOrientation);
  updateOrientation();

  const tryLockLandscape = (): void => {
    if (screen.orientation && typeof screen.orientation.lock === 'function') {
      screen.orientation.lock('landscape').catch(() => {});
    }
  };
  window.addEventListener('pointerdown', tryLockLandscape, { passive: true });

  if (import.meta.env.DEV) {
    const [components, bitecs, archetypes, collision] = await Promise.all([
      import('@src/ecs/components'),
      import('bitecs'),
      import('@src/ecs/archetypes'),
      import('@src/ecs/systems/CollisionSystem'),
    ]);
    const debug = { overlapStats: (scale = 1) => collision.enemyOverlapStats(ctx, scale) };
    (window as unknown as { __wa: unknown }).__wa = {
      ctx,
      roomDirector,
      restart: restartRun,
      goToTitle,
      pause,
      playCommercial,
      playDeathCinematic,
      playVictoryCinematic,
      forceWin: () => roomDirector.debugWin(),
      killPlayer: () => {
        const players = bitecs.query(ctx.world, [components.Player, components.Health]);
        if (players.length > 0) components.Health.hp[players[0]] = 0;
      },
      continues: () => ctx.continues,
      submitScore: (outcome: RunOutcome = 'death') =>
        submitRunScore({
          score: Math.max(1, ctx.score),
          floor: Math.max(1, ctx.floor.floor),
          room: Math.max(1, ctx.room.roomIndex),
          kills: ctx.kills,
          combo: ctx.combo.best,
          tier: difficulty().id,
          outcome,
        }),
      setFloor: (n: number) => {
        ctx.floor.floor = Math.max(1, n | 0);
      },
      components,
      archetypes,
      query: bitecs.query,
      render,
      audio,
      hud,
      loop,
      input,
      simSystems,
      debug,
    };
  }

  window.addEventListener('beforeunload', () => {
    pause.destroy();
    loop.stop();
    for (const s of presentation) s.destroy();
    for (const s of simSystems) s.destroy();
  });

  if (openingCut() !== 'none') prefetchOpeningArt();
  firstStart.setReady();

  // Session loop: title -> curtain -> arena -> title again (no page reload).
  for (let session = 0; ; session++) {
    const start: StartScreenHandle =
      session === 0
        ? firstStart
        : (() => {
            resetBootLayer(bootScreen, bootBeat, status);
            const next = showStartScreen({ host: bootScreen, status, audio: startAudio });
            next.setReady();
            void audio.playMenuMusic();
            return next;
          })();

    const choice = await start.choice;
    // The menu toggle forces the full story; otherwise the URL param / seen flag decides.
    const cut = choice.replayIntro ? 'full' : openingCut();
    try {
      if (cut !== 'none') audio.startIntroBed();
      else audio.leaveMenu();
    } catch (err) {
      console.warn('intro bed failed', err);
    }

    let began = false;
    const begin = (): void => {
      if (began) return;
      began = true;
      inArena = true;
      scoreSent = false;
      input.setTouchControlsVisible(true);
      start.dispose();
      audio.announcer.onCaption = (text, ms) => hud.showCaption(text, ms);
      roomDirector.startRun();
      if (!loop.isRunning) loop.start();
      loop.resume();
      pause.arm();
    };

    try {
      await playOpeningCurtain({
        boot: bootScreen,
        beat: bootBeat,
        cinematic: cut === 'none' ? undefined : () => playOpeningCinematic({ host: bootScreen, audio, cut }),
        handoff: () => {
          void audio.unlock().catch(() => undefined);
        },
        begin,
      });
    } catch (err) {
      console.warn('opening curtain failed', err);
      begin();
      bootScreen.classList.add('leaving', 'hidden');
    }
    if (!began) begin();

    await waitForTitleRequest();
  }
}

boot().catch((err: unknown) => {
  const status = document.getElementById('boot-status');
  if (status) status.textContent = `Boot failed: ${err instanceof Error ? err.message : String(err)}`;
  console.error(err);
});
